import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { Link as LinkIcon, Warning as WarningIcon } from '@mui/icons-material';

interface CancelWarningDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
    stockName: string;
}

export const CancelWarningDialog: React.FC<CancelWarningDialogProps> = ({
    open,
    onClose,
    onConfirm,
    loading,
    stockName,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            PaperProps={{
                sx: { borderRadius: 2 },
            }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningIcon color="warning" />
                    <Typography fontWeight="bold">Create New Stock Item?</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    Clicking Cancel will create a new stock item entry in your Stock Master
                    with the name:
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="primary"
                    sx={{ mt: 1, mb: 1 }}>
                    "{stockName}"
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    If this stock item already exists under a different name, consider mapping
                    it to the existing stock instead.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 2 }}>
                    Go Back
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="warning"
                    size="small"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}>
                    {loading ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        'Create New Stock Item'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface MapConfirmationDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    loading: boolean;
    extractedStockName: string;
    selectedStockName: string;
}

export const MapConfirmationDialog: React.FC<MapConfirmationDialogProps> = ({
    open,
    onClose,
    onConfirm,
    loading,
    extractedStockName,
    selectedStockName,
}) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            PaperProps={{
                sx: { borderRadius: 2 },
            }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <LinkIcon color="primary" />
                    <Typography fontWeight="bold">Confirm Stock Mapping</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary">
                    You are about to map the extracted stock item:
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="primary"
                    sx={{ mt: 1, mb: 1 }}>
                    "{extractedStockName}"
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    to the existing stock item:
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="success.main"
                    sx={{ mb: 1 }}>
                    "{selectedStockName}"
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Future invoices with this stock name will automatically be linked to
                    the selected stock item.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: 2 }}>
                    Go Back
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={loading}
                    sx={{ borderRadius: 2 }}>
                    {loading ? (
                        <CircularProgress size={18} color="inherit" />
                    ) : (
                        'Confirm Mapping'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
